import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Icon, Skeleton } from 'antd'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { AddOrCreateLoyaltyModal } from './AddOrCreateLoyaltyModal'
import { IBaseLoyaltyTier, PermissionNames } from '@getgreenline/homi-shared'
import { AppView, AppMainView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { Alert, Button, Dropdown, Menu, message, Tooltip } from 'antd-v4'
import { EditFilled, EllipsisOutlined, InfoCircleFilled, PlusOutlined } from '@ant-design/icons'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { DeleteLoyaltyModal } from './DeleteLoyaltyModal'
import { LoyaltySummarySection } from './LoyaltySummarySection'
import { RedemptionConversionRateModal } from './RedemptionConversionRateModal'
import { TooltipWrapper } from '../DashboardInventory/View/ActiveInventoryTable/InventoryDisplay/TooltipWrapper'
import { THEME_COLORS } from '@getgreenline/shared'
import { BLAZE_COMPANY } from '../../../constants/general'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  showModal: boolean | IBaseLoyaltyTier
  deleteModalLoyalty?: IBaseLoyaltyTier
  showLoyaltyPointsMultiplierModal: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class Loyalty extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      showModal: false,
      showLoyaltyPointsMultiplierModal: false,
    }
  }

  componentDidMount() {
    this.props.currentCompanyStore!.getLoyaltyTiers()
    this.props.currentCompanyStore!.refreshLoyaltyPointsMultiplier()
  }

  get canViewLoyaltyTiers() {
    const { currentCompanyStore } = this.props
    if (!currentCompanyStore) {
      return
    }

    return this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_LOYALTY_TIERS,
      currentCompanyStore!.company!.id,
    )
  }

  get canEditLoyaltyTiers() {
    const { currentCompanyStore, currentUserStore } = this.props

    const canEditLoyalties = currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_LOYALTY_TIERS,
      currentCompanyStore!.company!.id,
    )

    return canEditLoyalties
  }

  get canEditRedemptionRate() {
    const { currentUserStore, currentCompanyStore } = this.props

    const canEditRedemptionRate = currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_COMPANY_DETAILS,
      currentCompanyStore!.company!.id,
    )

    return canEditRedemptionRate
  }

  onAddClick = async () => {
    this.setState({ showModal: true })
  }

  onEditClick = async (loyaltyTier: IBaseLoyaltyTier) => {
    this.setState({
      showModal: loyaltyTier,
    })
  }

  onDeleteClick = async (loyaltyTier: IBaseLoyaltyTier) => {
    if (loyaltyTier.discounts.length > 0) {
      message.error(
        `This loyalty tier is associated to an active discount(s), please disassociate from discount before continuing with deletion`,
      )
      return
    }

    this.setState({ deleteModalLoyalty: loyaltyTier })
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const loyaltyTiers = this.props.currentCompanyStore!.sortedLoyaltyTiers

    if (!company.permissions.canAccessLoyalty) {
      return (
        <div
          className='d-flex p-5 align-items-center mt-5'
          style={{ alignItems: 'center', justifyContent: 'center' }}
        >
          <div>
            <h3>You do not have access to {BLAZE_COMPANY} Loyalty</h3>
            <p className='text-muted'>Please contact {BLAZE_COMPANY} support to request access</p>
          </div>
        </div>
      )
    }

    if (!this.canViewLoyaltyTiers) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_LOYALTY_TIERS} />
    }

    return (
      <AppView>
        <Helmet>
          <title>Customers | Loyalty</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <div className='full-view'>
          <AppHeader title='Loyalty'>
            <span className='spaced-button-wrapper'>
              <HelpLinkMenu
                helpLinkMenus={{
                  href: HelpLinks.LOYALTY,
                  title: 'Loyalty program',
                }}
              />
              {this.canEditLoyaltyTiers ? (
                <BlazeButton
                  buttonProps={{
                    className: `${this.canEditLoyaltyTiers ? '' : 'cursor-not-allowed'}`,
                    type: 'primary',
                    icon: 'plus',
                    onClick: this.onAddClick,
                    disabled: !this.canEditLoyaltyTiers,
                  }}
                  titleProps={{
                    title: 'Add loyalty tier',
                  }}
                />
              ) : (
                <Tooltip placement='left' title='insufficient permissions'>
                  <Button type='primary' icon={<PlusOutlined />} disabled>
                    Add loyalty tier
                  </Button>
                </Tooltip>
              )}
            </span>
            {this.state.deleteModalLoyalty && (
              <DeleteLoyaltyModal
                isVisible={this.state.deleteModalLoyalty !== undefined}
                loyaltyTier={this.state.deleteModalLoyalty}
                onCloseModal={() => this.setState({ deleteModalLoyalty: undefined })}
              />
            )}
          </AppHeader>
          <AppMainView className='intercom-pb-standard'>
            <Alert
              showIcon
              type='info'
              className='mb-3'
              message='Local regulations may vary, ensure you are compliant and check with your local regulators'
            />
            <div
              className='ml-3 mb-4 mt-2'
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            >
              <b style={{ fontSize: 16 }}>Loyalty Settings:</b>
              <Tooltip
                title={
                  <div>
                    <div>This conversion refers to the redemption of points in the POS.</div>
                  </div>
                }
              >
                <InfoCircleFilled className='ml-1' />
              </Tooltip>
              <div className='ml-5'>
                100 points is worth $
                {(this.props.currentCompanyStore!.loyaltyPointsMultiplier / 100).toFixed(2)}
              </div>
              <TooltipWrapper
                renderTooltip={!this.canEditRedemptionRate}
                tooltipProps={{
                  placement: 'left',
                  title: 'Insufficient permissions',
                }}
              >
                <Button
                  type={'ghost'}
                  icon={<EditFilled />}
                  className={'field-title-icon ml-3'}
                  onClick={() => this.setState({ showLoyaltyPointsMultiplierModal: true })}
                  title={'Edit'}
                  disabled={!this.canEditRedemptionRate}
                >
                  Edit
                </Button>
              </TooltipWrapper>
            </div>
            {loyaltyTiers ? (
              <table className='table'>
                <thead className='thead-light'>
                  <tr>
                    <th>Name</th>
                    <th>Customers</th>
                    <th>Summary</th>
                    <th>Options</th>
                  </tr>
                </thead>
                <tbody>
                  {loyaltyTiers.map((loyaltyTier) => {
                    return (
                      <tr
                        key={loyaltyTier.id}
                        className='category-row'
                        onClick={() => {
                          if (this.canEditLoyaltyTiers) {
                            this.onEditClick(loyaltyTier)
                          }
                        }}
                      >
                        <td>
                          <div>{loyaltyTier.name || 'No name'}</div>
                          {loyaltyTier.isDefault && (
                            <div style={{ color: THEME_COLORS.BRAND.retailAlt }}>Default</div>
                          )}
                        </td>
                        <td>
                          <div>{loyaltyTier.customerCount}</div>
                          {loyaltyTier.isDefault ? (
                            <div className='text-muted'>
                              All new customers will be a part of this loyalty tier
                            </div>
                          ) : undefined}
                        </td>
                        <td>
                          <LoyaltySummarySection loyaltyTier={loyaltyTier} />
                        </td>
                        <td>
                          <div onClick={(e) => e.stopPropagation()}>
                            <Dropdown
                              trigger={['click']}
                              getPopupContainer={() => document.body}
                              overlay={
                                <Menu style={{ width: 100 }}>
                                  <Menu.Item
                                    onClick={() => this.onEditClick(loyaltyTier)}
                                    className={`${
                                      this.canEditLoyaltyTiers ? '' : 'cursor-not-allowed'
                                    }`}
                                    disabled={!this.canEditLoyaltyTiers}
                                  >
                                    {this.canEditLoyaltyTiers ? (
                                      <div>Edit</div>
                                    ) : (
                                      <Tooltip placement='left' title='insufficient permissions'>
                                        <div>Edit</div>
                                      </Tooltip>
                                    )}
                                  </Menu.Item>
                                  <Menu.Item
                                    className={`${
                                      this.canEditLoyaltyTiers
                                        ? 'text-danger'
                                        : 'cursor-not-allowed'
                                    }`}
                                    onClick={() => this.onDeleteClick(loyaltyTier)}
                                    disabled={!this.canEditLoyaltyTiers}
                                  >
                                    {this.canEditLoyaltyTiers ? (
                                      <div>Delete</div>
                                    ) : (
                                      <Tooltip placement='left' title='insufficient permissions'>
                                        <div>Delete</div>
                                      </Tooltip>
                                    )}
                                  </Menu.Item>
                                </Menu>
                              }
                            >
                              <Button
                                icon={<EllipsisOutlined />}
                                style={{ transform: 'rotate(90deg)' }}
                              />
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <Skeleton active />
            )}

            {this.state.showLoyaltyPointsMultiplierModal && (
              <RedemptionConversionRateModal
                onClose={() => this.setState({ showLoyaltyPointsMultiplierModal: false })}
                visible={this.state.showLoyaltyPointsMultiplierModal}
              />
            )}

            {this.state.showModal ? (
              <AddOrCreateLoyaltyModal
                loyaltyTier={this.state.showModal}
                onHide={() => this.setState({ showModal: false })}
              />
            ) : undefined}
          </AppMainView>
        </div>
      </AppView>
    )
  }
}
